// LauncherHooksPanel：机器详情页的 sealed launcher 钩子安装/卸载面板。
// 对应旧 SSR machine_detail.html "Launcher 钩子管理"。
// 通过 POST /api/agents/{agent_id}/command 触发：
//   install_lua  → stub_version（可空=latest）
//   uninstall_lua→ 不可逆，需二次确认

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/forms/ConfirmDialog";
import { apiPostForm, APIError } from "@/api/client";
import { agentCommandPath } from "@/api/endpoints";
import { toast } from "@/components/ui/Sonner";

export interface LauncherHooksPanelProps {
  agentID: string;
  actor: string;
  canWrite: boolean;
}

export function LauncherHooksPanel({
  agentID,
  actor,
  canWrite,
}: LauncherHooksPanelProps) {
  const [stubVersion, setStubVersion] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [installConfirm, setInstallConfirm] = useState(false);
  const [uninstallConfirm, setUninstallConfirm] = useState(false);

  async function fire(command: string, params: Record<string, string> = {}) {
    setPending(command);
    try {
      const res = await apiPostForm<{ ok: boolean; error?: string }>(
        agentCommandPath(agentID),
        { command, ...params },
      );
      if (res.ok) {
        toast.success(`${command} 成功`, { description: `agent ${agentID}` });
      } else {
        toast.error(`${command} 失败`, {
          description: res.error ?? "未知错误",
        });
      }
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error(`${command} 异常`, { description: msg });
      throw err;
    } finally {
      setPending(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Launcher 钩子管理</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-xs">
        <p className="text-[hsl(var(--muted-foreground))]">
          sealed launcher 钩子（init_launcher / global_launcher /
          ModuleManager_launcher / RegisterWorldMediatorCommand）写入 cq2.exe
          cache，让客户端启动时进入 dev_bootstrap 模式、反连 daemon。
          业务 lua（dev_bootstrap / socket_repl / AutoMapHunt 等）不在 sealed
          bundle 里，由 daemon 内存分发，业务 lua 改动只要客户端断线-重连即可，
          不需要点这个按钮。
        </p>

        <div className="space-y-2 rounded border border-[hsl(var(--border))] p-3">
          <div className="font-medium">重装 launcher</div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="space-y-1">
              <span className="block text-[hsl(var(--muted-foreground))]">
                stub_version（留空 = latest）
              </span>
              <Input
                value={stubVersion}
                onChange={(e) => setStubVersion(e.target.value)}
                placeholder="stub_version"
                disabled={!canWrite}
                className="w-48"
              />
            </label>
            <Button
              type="button"
              size="sm"
              variant="default"
              disabled={!canWrite || pending !== null}
              onClick={() => setInstallConfirm(true)}
            >
              {pending === "install_lua" ? "…" : "重装 launcher"}
            </Button>
          </div>
        </div>

        <div className="space-y-2 rounded border border-[hsl(var(--destructive))] p-3">
          <div className="font-medium text-[hsl(var(--destructive))]">
            ⚠ 卸载 launcher（不可逆）
          </div>
          <p className="text-[hsl(var(--muted-foreground))]">
            删除 4 个 launcher 文件。卸载后客户端启动不会进入 dev_bootstrap
            模式（不反连 daemon、不加载 dev_bootstrap.lua / socket_repl.lua），
            只能跑原版 cq2.exe。恢复必须重新点"重装 launcher"。
          </p>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={!canWrite || pending !== null}
            onClick={() => setUninstallConfirm(true)}
          >
            {pending === "uninstall_lua" ? "…" : "卸载 launcher"}
          </Button>
        </div>
      </CardContent>

      <ConfirmDialog
        open={installConfirm}
        onOpenChange={setInstallConfirm}
        resource="agent"
        action="install_lua"
        target={agentID}
        actor={actor}
        confirmVariant="default"
        description={`重装 sealed launcher 到 agent ${agentID}；将覆盖 cq2.exe cache 中的 launcher 文件。`}
        onConfirm={async () => {
          const params: Record<string, string> = {};
          if (stubVersion.trim()) params.stub_version = stubVersion.trim();
          await fire("install_lua", params);
        }}
      />

      <ConfirmDialog
        open={uninstallConfirm}
        onOpenChange={setUninstallConfirm}
        resource="agent"
        action="uninstall_lua"
        target={agentID}
        actor={actor}
        description={`卸载 agent ${agentID} 上的 sealed launcher 钩子；客户端将无法再进入 dev_bootstrap 模式。`}
        onConfirm={async () => {
          await fire("uninstall_lua");
        }}
      />
    </Card>
  );
}
